import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {
  CreateTemplateRangeTierRequest,
  templateRangeTiersApi,
} from '../../../api/endpoints/metadata/templateRangeTiersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { request } from '@texas/utils/helpers/httpHelpers';
import { SharedDisclosureProps } from '@texas/components/shared/types';

export interface CreateVdcTierProps extends SharedDisclosureProps {
  onCreated: () => void;
}

const defaultValues: CreateTemplateRangeTierRequest = {
  name: '',
  startRange: 0,
  endRange: 0,
  costMultiplier: 1,
};

export function CreateVdcTier({
  onCreated,
  onClose,
  isOpen,
}: CreateVdcTierProps) {
  const toast = useToast();
  const { t } = useTranslation();
  const { request: createRequest, error } = useApiRequest(
    templateRangeTiersApi.create,
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateTemplateRangeTierRequest>({
    defaultValues: defaultValues,
  });

  const onSubmit = async (data: CreateTemplateRangeTierRequest) => {
    await request(createRequest, [data], () => {
      reset(defaultValues);
      toast({
        title: t('general.created'),
        status: 'success',
        isClosable: true,
      });
      onCreated();
    });
  };

  return (
    <Modal
      variant="entityDirectory"
      onClose={() => {
        reset(defaultValues);
        onClose();
      }}
      isOpen={isOpen}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader fontWeight="bold">
          {t('configuration.addNewVdcTier')}
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" gap={4}>
            {error && <ErrorDetails error={error} />}

            <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
              <VStack>
                <FormControl isRequired={true} isInvalid={!!errors.name}>
                  <TexasFormLabel>{t('general.name')}</TexasFormLabel>
                  <Input
                    variant="entityDirectory"
                    {...register('name', {
                      required: true,
                      maxLength: {
                        value: 100,
                        message: t('errors.maxLength', { count: 100 }),
                      },
                    })}
                    placeholder={t('general.name')}
                  />
                  <HookFormCounter
                    control={control}
                    name="name"
                    maxLength={100}
                  />
                  <ErrorLabel text={errors.name?.message} />
                </FormControl>
                <FormControl isRequired={true} isInvalid={!!errors.startRange}>
                  <TexasFormLabel>
                    {t('configuration.startRange')}
                  </TexasFormLabel>
                  <Input
                    type="number"
                    variant="entityDirectory"
                    {...register('startRange', {
                      required: true,
                      valueAsNumber: true,
                    })}
                    placeholder={t('configuration.startRange')}
                  />
                  <ErrorLabel text={errors.startRange?.message} />
                </FormControl>
                <FormControl isRequired={true} isInvalid={!!errors.endRange}>
                  <TexasFormLabel>{t('configuration.endRange')}</TexasFormLabel>
                  <Input
                    type="number"
                    variant="entityDirectory"
                    {...register('endRange', {
                      required: true,
                      valueAsNumber: true,
                    })}
                    placeholder={t('configuration.endRange')}
                  />
                  <ErrorLabel text={errors.endRange?.message} />
                </FormControl>
                <FormControl
                  isRequired={true}
                  isInvalid={!!errors.costMultiplier}
                >
                  <TexasFormLabel>
                    {t('configuration.costMultiplier')}
                  </TexasFormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    variant="entityDirectory"
                    {...register('costMultiplier', {
                      required: true,
                      valueAsNumber: true,
                    })}
                    placeholder={t('configuration.costMultiplier')}
                  />
                  <ErrorLabel text={errors.costMultiplier?.message} />
                </FormControl>
                <SubmitButton loading={isSubmitting}>
                  {t('general.save')}
                </SubmitButton>
              </VStack>
            </form>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
